import { useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import {
  Alert,
  Button,
  Card,
  Chip,
  SegmentedControl,
  Spinner,
  Tag,
  TextField,
} from "@/components/ui";
import { errorMessage } from "@/lib/api/error";
import { useGenerateSpeech } from "@/lib/api/queries";
import type { SpeechMode, SpeechOccasion } from "@/lib/api/types";
import { copyText } from "@/lib/clipboard";
import {
  isAcrosticNameReady,
  MAX_NAME_SYLLABLES,
  MIN_NAME_SYLLABLES,
  nameSyllables,
} from "@/lib/speech";

// 세그먼트 라벨은 고정이다 — 이름을 타이핑할 때마다 "3행시"→"4행시"로 흔들리면
// 탭이 움직여서 누르기 어려워진다. 실제 줄 수는 아래 도움말과 결과가 알려준다.
const MODES = [
  { value: "toast", label: "건배사" },
  { value: "acrostic", label: "n행시" },
];

/** 문구에 쓸 이름. 삼행시로 고정되지 않는다 — 이름 글자 수가 곧 행 수라서
 *  "강병욱" 은 3행시, "법조타운" 은 4행시다. 글자 수가 경계를 벗어난 동안에는
 *  숫자를 못 정하니 총칭 `n행시` 로 둔다. */
function modeLabel(mode: SpeechMode, name: string): string {
  if (mode === "toast") return "건배사";
  return isAcrosticNameReady(name) ? `${nameSyllables(name).length}행시` : "n행시";
}

// 자리를 자유 입력으로 받지 않는다 — 어휘와 금기를 가장 크게 가르는 입력이라
// 닫힌 목록이어야 서버 프롬프트가 예측 가능해진다(서버도 같은 목록만 받는다).
const OCCASIONS: SpeechOccasion[] = ["그냥 회식", "송년회", "승진·발령", "환영·환송", "MT·워크숍"];

/** 이름 칸의 안내 문구. n행시에서는 몇 행이 나올지까지 알려준다 — 이름 글자 수가
 *  곧 줄 수라는 게 이 모드의 유일한 구조적 규칙이라서 미리 보여주는 값이 있다. */
function nameHelperText(mode: SpeechMode, name: string): string {
  if (mode === "toast") return "비워도 돼요. 적으면 마지막 구호에서 불러 드려요.";
  const heads = nameSyllables(name);
  const range = `${MIN_NAME_SYLLABLES}~${MAX_NAME_SYLLABLES}글자`;
  if (heads.length === 0) return `한글 이름을 ${range}로 적어 주세요. 지점·팀 이름도 돼요.`;
  if (!isAcrosticNameReady(name)) {
    return `${range}로 적어 주세요. 지금은 ${heads.length}글자예요.`;
  }
  // 글자 수가 곧 행 수다. 3행으로 고정된 게 아니라는 걸 여기서 먼저 보여준다.
  return `${heads.join(" · ")}, ${heads.length}행시로 만들어요.`;
}

export default function ToastPage() {
  const [mode, setMode] = useState<SpeechMode>("toast");
  const [occasion, setOccasion] = useState<SpeechOccasion>("그냥 회식");
  const [name, setName] = useState("");
  const [extra, setExtra] = useState("");

  const generate = useGenerateSpeech();

  // 어떤 답으로 만든 결과인지는 mutation 이 들고 있다. 지금 폼 상태로 결과를
  // 설명하면, 만든 뒤에 이름만 바꿔 놓은 화면이 거짓말을 한다.
  const asked = generate.variables;
  const candidates = generate.data?.candidates ?? [];
  // 서버가 꼭 넣고 싶은 말을 **못 넣은 경우에만** 채워 보낸다. 평소엔 빈 문자열이다.
  const note = generate.data?.note ?? "";

  function handleModeChange(next: string) {
    setMode(next as SpeechMode);
    // 답 세 개는 그대로 둔다 — 셋 다 모드와 무관하고, 전환의 뜻은 "같은 회식,
    // 다른 형식"이다. 결과만 버린다: n행시 제목 아래 건배사가 남아 있으면 거짓말이다.
    generate.reset();
  }

  function handleGenerate() {
    generate.mutate(
      { mode, occasion, name, extra },
      {
        // 서버가 준 한국어 사유를 그대로 보여준다. 키 부재·머리글자 실패처럼
        // 사용자가 다음에 뭘 할지 아는 단서가 전부 detail 에 실려 온다.
        onError: (error) =>
          toast.error(errorMessage(error, "만들지 못했어요. 다시 시도해 주세요.")),
      }
    );
  }

  async function handleCopy(lines: string[]) {
    const ok = await copyText(lines.join("\n"));
    if (ok) toast.success(`${modeLabel(asked?.mode ?? mode, asked?.name ?? name)}를 복사했어요`);
    else toast.error("복사하지 못했어요");
  }

  const blocked = mode === "acrostic" && !isAcrosticNameReady(name);

  return (
    <AppShell title="건배사">
      <p className="text-body-2 text-label-alternative">
        세 가지만 알려 주시면 건배사와 n행시를 만들어 드려요.
      </p>

      <div className="mt-4">
        <SegmentedControl items={MODES} value={mode} onChange={handleModeChange} />
      </div>

      <Card variant="filled" padding={16} className="mt-4">
        <span className="text-label-1 font-semibold text-label-normal">무슨 자리예요?</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {OCCASIONS.map((value) => (
            <Chip key={value} active={occasion === value} onClick={() => setOccasion(value)}>
              {value}
            </Chip>
          ))}
        </div>

        <div className="mt-4">
          <TextField
            label="어떤 이름으로 만들까요?"
            placeholder="예) 임거묵, 강남지점"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText={nameHelperText(mode, name)}
          />
        </div>

        <div className="mt-4">
          <TextField
            label="꼭 넣고 싶은 말 있어요?"
            placeholder="예) 상반기 수신 1위"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            helperText="비워두면 자리 분위기로만 만들어요."
          />
        </div>
      </Card>

      {generate.isError && (
        <div className="mt-4">
          {/* 사진 추출과 달리 이 기능엔 "직접 입력" 대체 경로가 없다. 토스트로만
              알리면 사용자가 만들기를 다섯 번 누른다. 상태를 새로 만들지 않고
              mutation 의 에러를 그대로 그린다 — 다음 mutate 에서 저절로 사라진다. */}
          <Alert tone="cautionary">
            {errorMessage(generate.error, "만들지 못했어요. 다시 시도해 주세요.")}
          </Alert>
        </div>
      )}

      {generate.isPending ? (
        <div className="mt-4 flex items-center gap-2">
          <Spinner size={20} />
          <span className="text-body-2 text-label-alternative">
            {modeLabel(mode, name)}를 쓰고 있어요…
          </span>
        </div>
      ) : (
        <div className="mt-4">
          <Button fullWidth disabled={blocked} onClick={handleGenerate}>
            {candidates.length > 0 ? "다시 만들기" : "만들어 주세요"}
          </Button>
        </div>
      )}

      {candidates.length > 0 ? (
        <div className="mt-8">
          <h2 className="text-label-1 font-semibold text-label-normal">이렇게 준비해 봤어요</h2>
          {asked?.mode === "acrostic" && (
            <p className="mt-1 text-caption-1 text-label-alternative">
              {`${nameSyllables(asked.name).join("")} ${modeLabel("acrostic", asked.name)} · ${asked.occasion}`}
            </p>
          )}
          {note && (
            <div className="mt-3">
              {/* 키워드가 조용히 빠지면 사용자는 기능이 고장 난 줄 알고 다시 누른다.
                  사유가 보이면 다른 말로 바꿔서 누른다 — 그래서 결과 위에 붙인다. */}
              <Alert tone="info">{note}</Alert>
            </div>
          )}
          <div className="mt-3 flex flex-col gap-3">
            {/* 톤은 원래 셋이 하나씩이지만, 일부만 통과하면 같은 톤이 겹칠 수 있다
                — key 에 순번을 함께 넣는다. */}
            {candidates.map((candidate, index) => (
              <Card
                key={`${candidate.tone}-${index}`}
                padding={16}
                // 셋이 한꺼번에 뜨면 어느 걸 읽어야 할지 모른다. 순서대로
                // 들어오면 눈이 위에서 아래로 따라간다.
                className="animate-pop-in motion-reduce:animate-none"
                style={{ animationDelay: `${index * 70}ms` }}
              >
                <div className="flex items-center justify-between gap-2">
                  <Tag size="small">{candidate.tone}</Tag>
                  <Button variant="text" size="small" onClick={() => handleCopy(candidate.lines)}>
                    복사
                  </Button>
                </div>
                {/* 줄을 쪼개지 않는다. 머리글자만 따로 뽑아 칸을 만들면 "강물" 이
                    "강 | 물" 로 갈라진다 — n행시의 정상 케이스가 깨진다. */}
                <div className="mt-2 flex flex-col gap-1">
                  {candidate.lines.map((line, i) => (
                    <p key={`${candidate.tone}-${i}`} className="text-body-2 text-label-normal">
                      {line}
                    </p>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        !generate.isPending && (
          <p className="mt-3 text-caption-1 text-label-alternative">
            세 가지에 답하면 분위기가 다른 세 개를 만들어 드려요.
          </p>
        )
      )}
    </AppShell>
  );
}
